import React, { Component, ContextType, CSSProperties, ReactElement } from 'react';

import { BaseCardListContext, BaseCardListProps, BaseCardListState, Button, FetchContext, Loader, NotFound } from '@philips-emr/tws-components';
import { ButtonTheme, FirstPage, LayoutGap, SelectionType } from '@philips-emr/tws-constants';
import { Oswald, Value } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent, PageEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { SelectItemEvent } from '@philips-emr/tws-core/lib/component/types/selectable-list';
import { enumValues, getSlot, isEmptyArrayOrObject, isFunction, isMobile, propagateProps } from '@philips-emr/tws-utils';

import './base-card-list.css';

abstract class BaseCardList<
  Context extends BaseCardListContext,
  Props extends BaseCardListProps,
  State extends BaseCardListState<Context>> extends Component<Props, State> {
  static propTypes = {
    accumulate: bool,
    canChangeSelectionAction: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    columnGap: oneOf(enumValues(LayoutGap)).group('layout'),
    data: arrayOf(object).notEnumerable(),
    done: bool.notEnumerable(),
    emptyResultSubtitleExpression: expression,
    emptyResultTitleExpression: expression,
    firstPage: oneOf(enumValues(FirstPage)),
    keyField: string,
    lazy: bool,
    limit: number,
    loading: bool.notEnumerable(),
    loadMoreExpression: expression,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onNextPage: eventListener,
    onSelectItem: eventListener,
    onUnselectItem: eventListener,
    page: number.notEnumerable(),
    reset: bool.notEnumerable(),
    rowGap: oneOf(enumValues(LayoutGap)).group('layout'),
    selectable: bool,
    selectionType: oneOf(enumValues(SelectionType)),
    uuid: string,
  };

  static defaultProps = {
    canChangeSelectionAction: true,
    columnGap: LayoutGap.LARGE,
    done: true,
    firstPage: FirstPage.ZERO,
    keyField: 'id',
    limit: 12,
    loadMoreExpression: 'tws.deck.load-more',
    page: 0,
    reset: false,
    rowGap: LayoutGap.LARGE,
    selectable: false,
    selectionType: SelectionType.NONE,
  };

  state: State = {
    ...this.state,
    selectedCards: [],
  };

  abstract onBeforeUpdateCardSelection(event: SelectEvent<any>, card: Record<string, Value>): void;
  abstract setContext(): void;

  static contextType = FetchContext;

  context: ContextType<typeof FetchContext> | undefined;

  componentDidMount(): void {
    const { firstPage, lazy, limit } = this.props;

    this.setContext();

    if (lazy) {
      this.context?.setInitialPagination({ accumulate: false, limit, page: Number(firstPage) });
      return;
    }

    this.fetchFirstPage();
  }

  componentDidUpdate({ reset: prevReset }: Props): void {
    const { reset } = this.props;

    if (reset && prevReset !== reset) {
      this.fetchFirstPage();
    }
  }

  getContext(): Context {
    const { context } = this.state;

    if (!context) {
      throw Error('[BaseCardList]: Context not initialized.');
    }

    return context;
  }

  onLoadMoreClick = (): void => {
    const { limit, onNextPage, page } = this.props;

    if (isFunction(onNextPage)) {
      onNextPage(new PageEvent({ accumulate: true, limit, page: page + 1 }));
    }
  };

  onItemClick = (item: Record<string, Value>): void => {
    const { onClick } = this.props;

    if (isFunction(onClick)) {
      onClick(new ClickEvent({ value: item }));
    }
  };

  onDoubleClick = (): void => {
    const { onDoubleClick } = this.props;

    if (isFunction(onDoubleClick)) {
      onDoubleClick();
    }
  };

  onContextMenuClick = (): void => {
    const { onContextMenuClick } = this.props;

    if (isFunction(onContextMenuClick)) {
      onContextMenuClick();
    }
  };

  fetchFirstPage(): void {
    const { lazy, limit, onNextPage, firstPage } = this.props;

    if (lazy) {
      return;
    }

    if (isFunction(onNextPage)) {
      onNextPage(new PageEvent({ accumulate: false, limit, page: Number(firstPage) }));
    }
  }

  modifyCardSelection(card: Record<string, Value>, remove: boolean): string[] {
    const { selectionType = BaseCardList.defaultProps.selectionType } = this.props;
    const { selectedCards } = this.state;

    return this.getContext().modifyCardSelection({
      item: this.getCardKey(card),
      remove,
      selection: selectedCards,
      type: selectionType,
    });
  }

  onCardSelect = ({ item: card }: SelectItemEvent<Record<string, Value>>): void => {
    const { onSelectItem } = this.props;

    const items = this.modifyCardSelection(card, false);

    this.setState({ selectedCards: items }, () => {
      if (isFunction(onSelectItem)) {
        onSelectItem(new SelectItemEvent({ item: card }));
      }
    });
  };

  onCardUnselect = ({ item: card }: SelectItemEvent<Record<string, Value>>): void => {
    const { onUnselectItem } = this.props;
    const { selectedCards } = this.state;

    const items = this.modifyCardSelection(card, true);

    if (items.length !== selectedCards.length) {
      this.setState({
        selectedCards: items,
      }, () => {
        if (isFunction(onUnselectItem)) {
          onUnselectItem(new SelectItemEvent({ item: card }));
        }
      });
    }
  };

  updateCardSelection = (event: SelectEvent<any>, card: Record<string, Value>): void => {
    const eventFunction = event.selected ? this.onCardSelect : this.onCardUnselect;

    this.onBeforeUpdateCardSelection(event, card);

    eventFunction(new SelectItemEvent({ item: card }));
  };

  getCardKey(card: Record<string, Value>): string {
    const { keyField } = this.props;

    return String(card[keyField]);
  }

  renderLoadMore(): ReactElement | null {
    const { data, done, loadMoreExpression, loading } = this.props;

    if (!loading && !done && data?.length && !Oswald.isDesigning()) {
      return (
        <Button
          className="c-base-card-list__load-more"
          onClick={this.onLoadMoreClick}
          onContextMenuClick={this.onContextMenuClick}
          onDoubleClick={this.onDoubleClick}
          theme={ButtonTheme.SECONDARY}
          value={loadMoreExpression}
        />
      );
    }

    return null;
  }

  renderNotFound(): ReactElement | null {
    const { data, emptyResultSubtitleExpression, emptyResultTitleExpression, loading } = this.props;

    if (Oswald.isDesigning() || !isEmptyArrayOrObject(data) || loading) {
      return null;
    }

    return (
      <NotFound
        className="c-base-card-list__not-found"
        subtitleExpression={emptyResultSubtitleExpression}
        titleExpression={emptyResultTitleExpression}
      />
    );
  }

  renderLoading(): ReactElement | null {
    const { loading } = this.props;

    if (!loading || Oswald.isDesigning()) {
      return null;
    }

    return (
      <Loader centered className="c-base-card-list__loader u-hide@empty" size={isMobile() ? 32 : 64} />
    );
  }

  isCardSelectable(): boolean {
    const { selectable, selectionType = BaseCardList.defaultProps.selectionType } = this.props;

    return this.getContext().allowCardSelection(selectable, selectionType);
  }

  renderCards(cardsData: Record<string, Value>[]): ReactElement | null {
    const {

      columnGap,
      rowGap,
      selectionType,
    } = this.props;
    const { selectedCards } = this.state;

    let { children } = this.props;

    if (!children) {
      return null;
    }

    children = getSlot(children, 'content');

    const cardListStyle = {
      '--col-gap': columnGap,
      '--row-gap': rowGap,
    } as CSSProperties;

    return (
      <div className="o-grid-card-list c-deck__cards-wrapper" style={cardListStyle}>
        {cardsData.map((card) => {
          const cardKeyFieldValue = this.getCardKey(card);

          const cardProps = {
            ...card,
            identifier: cardKeyFieldValue,
            onClick: () => this.onItemClick(card),
            onDoubleClick: this.onDoubleClick,
            onMenuClick: this.onContextMenuClick,
            onSelect: (event: SelectEvent<any>) => this.updateCardSelection(event, card),
            selectable: this.isCardSelectable(),
            selected: this.getContext().isCardSelected(cardKeyFieldValue, selectedCards),
            selectionType,
          };

          return propagateProps(children, cardProps, true);
        })}
      </div>
    );
  }

}

export default BaseCardList;
